// react
import type { ReactNode } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, Tooltip } from '@mui/material';

// components
import { useCurrentUserAccess } from '@/hooks/useCurrentUserAccess';

// relative
import { hasAccess, UserRole } from './userRole';

type AccessGuardProps = {
  children: ReactNode;
  minimumRole?: UserRole;
  requireSuperuser?: boolean;
  /**
   * Element to render when access is denied.
   * - Omit (default): shows children with a greyed-out overlay and tooltip.
   * - Pass `null` to hide completely.
   * - Pass a ReactNode to show a custom fallback.
   *
   * Note: `SuperuserOnly` always hides — the overlay is never shown for superuser gates.
   */
  fallback?: ReactNode;
  /** Override the default tooltip message shown on the disabled overlay. */
  message?: string;
  /** Set to false to suppress the tooltip on the disabled overlay. Defaults to true. */
  showTooltip?: boolean;
};

export function AccessGuard({
  children,
  minimumRole,
  requireSuperuser = false,
  fallback,
  message,
  showTooltip = true,
}: AccessGuardProps) {
  const { t } = useTranslation('common');
  const { role, isSuperuser } = useCurrentUserAccess();

  const allowed = hasAccess({
    currentRole: role,
    minimumRole,
    isSuperuser,
    requireSuperuser,
  });

  if (allowed) return children;

  // Superuser gates are internal infrastructure — never show an overlay, just hide.
  if (requireSuperuser) return fallback ?? null;

  // Explicit fallback provided — show it instead of the overlay.
  if (fallback !== undefined) return fallback;

  // No fallback: show the children with a disabled overlay and tooltip.
  const defaultMessage =
    minimumRole === UserRole.Administrator
      ? t('permissions.adminOnly')
      : t('permissions.readOnlyRestriction');

  const overlayContent = (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
          cursor: 'not-allowed',
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      />
      <Box sx={{ opacity: 0.4, pointerEvents: 'none' }}>{children}</Box>
    </Box>
  );

  if (!showTooltip) return overlayContent;

  return <Tooltip title={message ?? defaultMessage}>{overlayContent}</Tooltip>;
}

type GuardProps = {
  children: ReactNode;
  fallback?: ReactNode;
  message?: string;
  showTooltip?: boolean;
};

/** Renders children only for users with at least the User role (i.e. not read-only). */
export function WritableOnly(props: GuardProps) {
  return <AccessGuard minimumRole={UserRole.User} {...props} />;
}

/** Renders children only for users with the Administrator role. */
export function AdministratorOnly(props: GuardProps) {
  return <AccessGuard minimumRole={UserRole.Administrator} {...props} />;
}

/** Renders children only for platform superusers. Always hides when denied — no overlay. */
export function SuperuserOnly(props: GuardProps) {
  return <AccessGuard requireSuperuser {...props} />;
}
